import React, { useState, useMemo } from 'react';
import { Column, Row, Expander } from '@amzn/meridian';

import { getCycleDisplayName } from '../../utils/helpers';
import ConfigPanel from './shared/ConfigPanel';

import './VolumeConfiguration.css';

// Operator glyph makes the per-cycle Total read as a formula:
// Total = Planned + Sideline/RTS − Rollover.
const VOLUME_TYPES = [
  { key: 'plannedVolume', label: 'Planned', op: null },
  { key: 'sidelineRTS', label: 'Sideline', op: 'add' },
  { key: 'plannedRollover', label: 'Rollover', op: 'sub' }
];

// Per-cycle slice colors, cycled if there are ever more than four cycles.
const CYCLE_COLORS = ['var(--cyc-1)', 'var(--cyc-2)', 'var(--cyc-3)', 'var(--cyc-4)'];

const getCycleTotalVolume = (cycle) => {
  const plannedVolume = parseFloat(cycle.plannedVolume) || 0;
  const sidelineRTS = parseFloat(cycle.sidelineRTS) || 0;
  const plannedRollover = parseFloat(cycle.plannedRollover) || 0;
  return plannedVolume + sidelineRTS - plannedRollover;
};

// Donut conic-gradient from each cycle's (non-negative) share. Normalizes against
// the sum of positive totals so slices always sum to 100% even if some cycles are negative.
const buildDonutGradient = (cycles) => {
  const positiveSum = cycles.reduce((s, c) => s + Math.max(getCycleTotalVolume(c), 0), 0);
  if (positiveSum <= 0) return 'var(--border-row)';
  let acc = 0;
  const stops = cycles.map((cycle, i) => {
    const share = (Math.max(getCycleTotalVolume(cycle), 0) / positiveSum) * 100;
    const stop = `${CYCLE_COLORS[i % CYCLE_COLORS.length]} ${acc}% ${acc + share}%`;
    acc += share;
    return stop;
  });
  return `conic-gradient(${stops.join(', ')})`;
};

const VolumeInput = ({ value, onChange }) => (
  <span className="fp-input vc-input">
    <input type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder="0" />
  </span>
);

const VolumeConfiguration = ({ totalVolume, onTotalVolumeChange, joined = false }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleVolumeChange = (cycleIndex, volumeKey, value) => {
    onTotalVolumeChange(
      totalVolume.map((cycle, i) => (i === cycleIndex ? { ...cycle, [volumeKey]: parseFloat(value) || 0 } : cycle))
    );
  };

  const summary = useMemo(
    () =>
      totalVolume.map((c) => ({
        label: getCycleDisplayName(c.name),
        total: getCycleTotalVolume(c).toLocaleString()
      })),
    [totalVolume]
  );

  const grandTotal = useMemo(() => totalVolume.reduce((sum, c) => sum + getCycleTotalVolume(c), 0), [totalVolume]);
  const donutGradient = useMemo(() => buildDonutGradient(totalVolume), [totalVolume]);

  return (
    <ConfigPanel joined={joined} className="vc-config">
      <Row widths={['fill', 'fit']} alignmentVertical="center">
        <Expander
          open={isExpanded}
          onChange={setIsExpanded}
          title="Total Volume Configuration"
          type="inline"
          spacingInset="300">
          <Column spacing="300" alignmentHorizontal="stretch" width="100%">
            <div className="vc-layout">
              <div className="vc-donut-wrap">
                <div className="vc-donut" style={{ '--vc-donut-gradient': donutGradient }}>
                  <div className="vc-donut-hole">
                    <span className="vc-grand">{grandTotal.toLocaleString()}</span>
                    <span className="vc-grand-label">total volume</span>
                  </div>
                </div>
              </div>
              <table className="vc-table">
                <thead>
                  <tr>
                    <th className="vc-th vc-th-cycle">Cycle</th>
                    {VOLUME_TYPES.map((volType) => (
                      <th key={volType.key} className="vc-th vc-th-num">
                        {volType.op === 'add' && <span className="vc-op add">+</span>}
                        {volType.op === 'sub' && <span className="vc-op sub">−</span>}
                        {volType.label}
                      </th>
                    ))}
                    <th className="vc-th vc-th-total">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {totalVolume.map((cycle, cycleIndex) => {
                    const total = getCycleTotalVolume(cycle);
                    const share = grandTotal > 0 ? Math.max(0, Math.round((total / grandTotal) * 100)) : 0;
                    const color = CYCLE_COLORS[cycleIndex % CYCLE_COLORS.length];
                    return (
                      <tr key={cycleIndex} className="vc-row">
                        <td className="vc-cell vc-cell-cycle">
                          <div className="vc-cycle">
                            <span className="vc-dot" style={{ background: color }} />
                            <span className="vc-cycle-name">{getCycleDisplayName(cycle.name)}</span>
                            <span className="vc-share-meter">
                              <i style={{ width: `${share}%`, background: color }} />
                            </span>
                            <span className="vc-cycle-share">{share}% of station</span>
                          </div>
                        </td>
                        {VOLUME_TYPES.map((volType) => (
                          <td key={volType.key} className="vc-cell vc-cell-num">
                            <VolumeInput
                              value={cycle[volType.key]}
                              onChange={(value) => handleVolumeChange(cycleIndex, volType.key, value)}
                            />
                          </td>
                        ))}
                        <td className="vc-cell vc-cell-num">
                          <span className={`vc-total${total === 0 ? ' is-zero' : ''}`}>{total.toLocaleString()}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Column>
        </Expander>
        {!isExpanded && summary.length > 0 && (
          <div className="fp-collapsed-summary">
            <div className="fp-collapsed-chips">
              {summary.map((item, idx) => (
                <span key={idx} className="fp-chip">
                  <span className="fp-chip-dot" style={{ background: CYCLE_COLORS[idx % CYCLE_COLORS.length] }} />
                  <span className="fp-chip-label">{item.label}</span>
                  <span className="fp-chip-value">{item.total}</span>
                </span>
              ))}
            </div>
            <div className="vc-summary-total">
              <span className="vc-summary-total-value">{grandTotal.toLocaleString()}</span>
              <span className="vc-summary-total-label">total</span>
            </div>
          </div>
        )}
      </Row>
    </ConfigPanel>
  );
};

export default VolumeConfiguration;
